import type { Edulead } from 'edulead';
import { useCallback, useEffect, useRef, useState } from 'react';
import { allPages, enumLabel } from '../services/edulead-v4';
import { eduleadV4 } from '../services/edulead-v4-client';
import { isMatchRunActive } from '../services/match-runs.service';
import { subscribeToMatchRun } from '../services/pusher.service';

/** Live counters, which arrive ahead of the persisted ones. */
export interface MatchRunProgress {
  step?: 'FETCHING' | 'MATCHING' | 'FINALIZING';
  processed?: number;
  total?: number;
  matched?: number;
}

const POLL_INTERVAL_MS = 20_000;

/**
 * Loads a match run and follows it while it works.
 *
 * State is seeded from the API before subscribing, so reloading mid-run renders
 * immediately rather than waiting for the next event.
 *
 * It also polls while the run is active, which the export flow does not. That is
 * deliberate: the Pusher channel has no replay, and a match run lasts tens of minutes
 * rather than seconds, so a single dropped completion event would leave the page claiming
 * a finished run is still going. Polling one open page every 20s is a cheap way to remove
 * that whole class of bug.
 */
export function useMatchRun(runId: string | undefined) {
  const [run, setRun] = useState<Edulead.EduleadV4Match | undefined>();
  const [exportLimit, setExportLimit] = useState<number | undefined>();
  const [progress, setProgress] = useState<MatchRunProgress>({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | undefined>();

  // Read inside the interval without making it a dependency, which would tear down and
  // recreate the timer on every tick.
  const statusRef = useRef<string | undefined>();
  const status = enumLabel(run?.state, 'MATCH_STATE_');
  statusRef.current = status;

  const refresh = useCallback(async () => {
    if (!runId) return;

    try {
      const response = await eduleadV4.matches.get({ id: runId });
      setRun(response.match);
      setError(undefined);
    } catch (err) {
      setError(err as Error);
    } finally {
      setIsLoading(false);
    }
  }, [runId]);

  useEffect(() => {
    setIsLoading(true);
    void refresh();
  }, [refresh]);

  // The institution export limit is the team's, not the run's: read once from the limits.
  useEffect(() => {
    allPages(eduleadV4.limits.list({ page_size: 100, 'filter.period': 'LIMIT_PERIOD_ONE_OFF' }))
      .then(limits => {
        const value = limits.find(l => l.id === 'CSV_EXPORT_NR_INSTITUTIONS__ONEOFF')?.value;
        setExportLimit(value != null ? Number(value) : undefined);
      })
      .catch(err => console.error('Failed to load the export limit:', err));
  }, []);

  useEffect(() => {
    if (!runId || !isMatchRunActive(status)) return;

    const unsubscribe = subscribeToMatchRun(runId, {
      onStep: data => setProgress(current => ({ ...current, step: data.step })),
      onProgress: data => setProgress({
        step: data.step,
        processed: data.processed,
        total: data.total,
        matched: data.matched,
      }),
      // Don't trust the event's payload for terminal state - re-read, so the page and the
      // database cannot disagree.
      onCompleted: () => void refresh(),
      onFailed: () => void refresh(),
    });

    const poll = setInterval(() => {
      if (isMatchRunActive(statusRef.current)) void refresh();
    }, POLL_INTERVAL_MS);

    return () => {
      unsubscribe();
      clearInterval(poll);
    };
  }, [runId, status, refresh]);

  return {
    run,
    /** The state as the page's tags name it (READY, FAILED, …). */
    status,
    stats: run?.stats,
    exportLimit,
    progress,
    isActive: isMatchRunActive(status),
    isLoading,
    error,
    refresh,
  };
}
